import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { clsx } from 'clsx';
import { IconButton } from '@design-system/components/button/iconButton';
import { Checkbox } from '@mui/material';
import {
  faCaretDown,
  faCaretRight,
  faDumbbell,
  faRepeat,
  faSquareCheck,
  faTrash,
  faVideo,
} from '@fortawesome/pro-solid-svg-icons';
import _ from 'lodash';
import { sprinkles } from '@design-system/sprinkles.css';
import { bgColorsVars } from '@design-system/vars.css';
import { Exercise as ExerciseEnum } from '@playbook-technologies/common-enums';
import {
  Circuit,
  LegacyExercise,
  WorkoutItem,
} from '../../models/workout.domain';

type Exercises = Array<LegacyExercise>;

export const ClipboardPanelItem = ({
  data,
  index,
  deleteItem,
  selected,
  addSelection,
  removeSelection,
}: {
  data: WorkoutItem;
  index: number;
  deleteItem: (index: number) => void;
  selected: boolean;
  addSelection: (index: number) => void;
  removeSelection: (index: number) => void;
}) => {
  const [showCircuitDetails, setShowCircuitDetails] = useState(false);
  const isCircuit = data.type === ExerciseEnum.Type.Multiple.value;

  const onSelectionChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    checked ? removeSelection(index) : addSelection(index);
  };

  const circuitExercisesLength = (data as Circuit)?.exercises?.length;
  return (
    <div className="group hover:bg-[#EEE] px-4">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Checkbox
              checked={selected}
              onChange={onSelectionChange}
              checkedIcon={
                <FontAwesomeIcon icon={faSquareCheck} className="w-6 h-6" />
              }
              sx={{
                padding: 0,
                '&.MuiCheckbox-root.Mui-checked > svg.MuiSvgIcon-root > path': {
                  fill: bgColorsVars['N-100'],
                },
                '&.MuiCheckbox-root> svg.MuiSvgIcon-root > path': {
                  fill: bgColorsVars['N-100'],
                },
              }}
            />
            <IconButton
              variant="text"
              color="neutral"
              icon={getListIcon(data.type)}
            />
            <span className="w-[245px] text-ellipsis whitespace-nowrap overflow-hidden font-bold">
              {`${data.name}${
                circuitExercisesLength
                  ? ' (' + circuitExercisesLength + ')'
                  : ''
              }`}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-[16px] h-[42px] flex justify-center items-center">
              {isCircuit ? (
                <div
                  className="cursor-pointer"
                  onClick={() => setShowCircuitDetails((prev) => !prev)}
                >
                  <FontAwesomeIcon
                    icon={showCircuitDetails ? faCaretDown : faCaretRight}
                    className="text-[18px]"
                  />
                </div>
              ) : null}
            </div>
            <div className="w-[18px] h-[42px] flex justify-center items-center">
              <div
                className="hidden group-hover:flex cursor-pointer"
                onClick={() => deleteItem(index)}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  className={clsx('text-[18px]', sprinkles({ color: 'N-80' }))}
                />
              </div>
            </div>
          </div>
        </div>
        {showCircuitDetails ? (
          <div>
            <ClipboardPanelSubItem exercises={(data as Circuit)?.exercises} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const getListIcon = (type: number) => {
  if (type === ExerciseEnum.Type.Multiple.value) return faRepeat;
  if (type === ExerciseEnum.Type.YouTube.value) return faVideo;
  return faDumbbell;
};

const ClipboardPanelSubItem = ({ exercises }: { exercises?: Exercises }) => {
  if (!exercises) return null;

  return (
    <div>
      {exercises.map((ex) => (
        <div
          key={ex.name}
          className="flex py-2 pr-2 pl-[40px] gap-4 items-center"
        >
          <IconButton
            variant="text"
            color="neutral"
            icon={faDumbbell}
            iconClassName={sprinkles({ color: 'N-80' })}
          />
          <span
            className={clsx(
              'w-[200px] text-ellipsis whitespace-nowrap overflow-hidden font-bold',
              sprinkles({ color: 'N-80' }),
            )}
          >
            {ex.name}
          </span>
        </div>
      ))}
    </div>
  );
};
